import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Users, Settings2 } from "lucide-react";
import PromoStrip from "@/components/site/PromoStrip";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const PLUS = (d) => new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);

export default function Cars() {
  const { format: fmt } = useCurrency();
  const [form, setForm] = useState({ city: "New Delhi", pickup_date: PLUS(7), return_date: PLUS(10) });
  const [cars, setCars] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  const search = async (e) => {
    e?.preventDefault();
    const { data } = await api.post("/cars/search", form);
    setCars(data.cars);
  };
  useEffect(() => { search(); // eslint-disable-next-line
  }, []);

  const days = Math.max(1, Math.round((new Date(form.return_date) - new Date(form.pickup_date)) / 86400000));

  const book = async (c) => {
    if (!user) { toast.error("Please sign in to continue"); return; }
    const total = c.price * days;
    const { data } = await api.post("/bookings", {
      booking_type: "car", item: { ...c, days, ...form },
      travelers: [{ name: user.name }], total_amount: total, currency: "USD",
      contact: { email: user.email, name: user.name },
    });
    navigate(`/checkout/${data.id}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] rounded-3xl p-6 md:p-8 text-white">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold">Drive your own pace</h1>
        <form onSubmit={search} className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 bg-white/95 p-4 rounded-2xl text-slate-700">
          <div className="md:col-span-4">
            <Label>Pickup city</Label>
            <Input data-testid="car-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required className="h-11" />
          </div>
          <div className="md:col-span-3">
            <Label>Pickup date</Label>
            <Input data-testid="car-pickup" type="date" value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} className="h-11" />
          </div>
          <div className="md:col-span-3">
            <Label>Return date</Label>
            <Input data-testid="car-return" type="date" min={form.pickup_date} value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} className="h-11" />
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button data-testid="car-search-submit" type="submit" className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] h-11 rounded-full">Search</Button>
          </div>
        </form>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 stagger">
        {cars.map((c) => (
          <Card key={c.id} data-testid={`car-card-${c.id}`} className="overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all">
            <div className="h-44 bg-slate-100"><img src={c.image} alt={c.model} className="w-full h-full object-cover" /></div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">{c.model}</h3>
                <span className="text-[11px] px-2 py-1 rounded-full bg-teal-50 text-teal-700 font-medium">{c.category}</span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {c.seats} seats</span>
                <span className="flex items-center gap-1"><Settings2 className="w-4 h-4" /> {c.transmission}</span>
              </div>
              <div className="flex items-end justify-between mt-4">
                <div>
                  <div className="font-heading text-2xl font-bold text-[var(--brand-accent)]">{fmt(c.price, { decimals: 0 })}</div>
                  <div className="text-xs text-slate-500">per day · {days} day{days > 1 ? "s" : ""}</div>
                </div>
                <Button data-testid={`car-book-${c.id}`} onClick={() => book(c)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">Book</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <PromoStrip />
    </div>
  );
}
